// ===== 1. Imports =====
import React, { useState, useMemo } from "react";
import POPanel from "./POPanel";
import POModal from "./POModal";
import InvoicePanel from "./InvoicePanel";
import InvoiceModal from "./InvoiceModal";
import { flattenInvoices } from "./utils/poUtils";
import { canManageStatus, canCompleteInvoice } from "./utils/rolePermissions";
import { Wallet, ClipboardCheck } from "lucide-react";

export default function DashboardPage({
  projectCode,
  suppliers = [],
  accounts = [],
  departments = [],
  users = [],
  userName,
  userRole,
  department,
  setActiveTab,
}) {
  const [pos, setPos] = useState([]);
  const [invoicesIndex, setInvoicesIndex] = useState({});
  const [selectedPO, setSelectedPO] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [fullscreenPanel, setFullscreenPanel] = useState(null);

  const session = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("filma_session") || "{}");
    } catch {
      return {};
    }
  }, []);

  const effectiveUserName = userName || session.userName || "Usuario";
  const effectiveUserRole = userRole || session.userRole || "PM";
  const effectiveDept = department || session.department || "";

  const flatInvoices = useMemo(
    () => flattenInvoices(invoicesIndex, pos),
    [invoicesIndex, pos]
  );

  return (
    <section className="container" style={{ padding: "20px 0 30px" }}>
      {["PM", "Controller", "HOD"].includes(effectiveUserRole) && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            marginTop: 4,
            marginBottom: 16,
          }}
        >
          <button
            className="button"
            title="Revisión de facturas"
            onClick={() => setActiveTab("REVISION")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 32,
              padding: "6px 10px",
              fontSize: 13,
            }}
          >
            <ClipboardCheck size={15} />
            Aprobaciones
          </button>

          {effectiveUserRole === "Controller" && (
            <button
              className="button button--primary"
              title="Panel de pagos"
              onClick={() => setActiveTab("PAGOS")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 32,
                padding: "6px 10px",
                fontSize: 13,
                backgroundColor: "#16a34a",
                color: "white",
                border: "none",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#15803d")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#16a34a")
              }
            >
              <Wallet size={15} />
              Pagos
            </button>
          )}
        </div>
      )}

      <div style={{ width: "100%", margin: "0 auto" }}>
        {!fullscreenPanel && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 24,
              alignItems: "start",
            }}
          >
            <POPanel
              projectCode={projectCode}
              pos={pos}
              setPos={setPos}
              departments={departments}
              users={users}
              suppliers={suppliers}
              accounts={accounts}
              userRole={effectiveUserRole}
              userName={effectiveUserName}
              department={effectiveDept}
              setSelectedPO={setSelectedPO}
              setInvoicesIndex={setInvoicesIndex}
              onFullscreen={() => setFullscreenPanel("po")}
            />

            <InvoicePanel
              projectCode={projectCode}
              pos={pos}
              invoicesIndex={invoicesIndex}
              flatInvoices={flatInvoices}
              userRole={effectiveUserRole}
              userCanCompleteInvoice={canCompleteInvoice(effectiveUserRole)}
              onEditInvoice={setEditingInvoice}
              onFullscreen={() => setFullscreenPanel("invoice")}
            />
          </div>
        )}

        {fullscreenPanel === "po" && (
          <POPanel
            projectCode={projectCode}
            pos={pos}
            setPos={setPos}
            departments={departments}
            users={users}
            suppliers={suppliers}
            accounts={accounts}
            userRole={effectiveUserRole}
            userName={effectiveUserName}
            department={effectiveDept}
            setSelectedPO={setSelectedPO}
            setInvoicesIndex={setInvoicesIndex}
            onFullscreen={() => setFullscreenPanel(null)}
            isFullscreen
          />
        )}

        {fullscreenPanel === "invoice" && (
          <InvoicePanel
            projectCode={projectCode}
            pos={pos}
            invoicesIndex={invoicesIndex}
            flatInvoices={flatInvoices}
            userRole={effectiveUserRole}
            userCanCompleteInvoice={canCompleteInvoice(effectiveUserRole)}
            onEditInvoice={setEditingInvoice}
            onFullscreen={() => setFullscreenPanel(null)}
            isFullscreen
          />
        )}
      </div>

      {selectedPO && (
        <POModal
          projectCode={projectCode}
          po={selectedPO}
          invoicesIndex={invoicesIndex}
          userRole={effectiveUserRole}
          userName={effectiveUserName}
          userCanManageStatus={canManageStatus(effectiveUserRole)}
          onClose={() => setSelectedPO(null)}
          onEditInvoice={setEditingInvoice}
        />
      )}
      {editingInvoice && (
        <InvoiceModal
          projectCode={projectCode}
          editingInvoice={editingInvoice}
          setEditingInvoice={setEditingInvoice}
          userCanCompleteInvoice={canCompleteInvoice(effectiveUserRole)}
          userRole={effectiveUserRole}
        />
      )}
    </section>
  );
}
